/**
 * Performance Metrics Definitions
 * 
 * This module defines all performance metrics to be tracked across the application.
 * Metrics are categorized by their purpose and use case.
 */

export interface PerformanceMetric {
  name: string;
  category: MetricCategory;
  unit: MetricUnit;
  threshold?: {
    good: number;
    acceptable: number;
    poor: number;
  };
  description: string;
}

export enum MetricCategory {
  // Core Web Vitals
  CORE_WEB_VITALS = 'core_web_vitals',
  
  // Recording performance
  RECORDING = 'recording',
  
  // Upload performance
  UPLOAD = 'upload',
  
  // Canvas rendering
  CANVAS = 'canvas',
  
  // Database operations
  DATABASE = 'database',
  
  // Worker performance
  WORKER = 'worker',
  
  // User interaction
  INTERACTION = 'interaction',
}

export enum MetricUnit {
  MILLISECONDS = 'ms',
  SECONDS = 's',
  BYTES = 'bytes',
  MEGABYTES = 'mb',
  PERCENTAGE = '%',
  COUNT = 'count',
  FPS = 'fps',
}

/**
 * Defined Performance Metrics
 */
export const PERFORMANCE_METRICS: Record<string, PerformanceMetric> = {
  // Core Web Vitals
  FCP: {
    name: 'First Contentful Paint',
    category: MetricCategory.CORE_WEB_VITALS,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 1800,
      acceptable: 3000,
      poor: 3000,
    },
    description: 'Time until first content is painted on screen',
  },
  LCP: {
    name: 'Largest Contentful Paint',
    category: MetricCategory.CORE_WEB_VITALS,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 2500,
      acceptable: 4000,
      poor: 4000,
    },
    description: 'Time until largest content element is painted',
  },
  CLS: {
    name: 'Cumulative Layout Shift',
    category: MetricCategory.CORE_WEB_VITALS,
    unit: MetricUnit.COUNT,
    threshold: {
      good: 0.1,
      acceptable: 0.25,
      poor: 0.25,
    },
    description: 'Visual stability score (lower is better)',
  },
  FID: {
    name: 'First Input Delay',
    category: MetricCategory.CORE_WEB_VITALS,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 100,
      acceptable: 300,
      poor: 300,
    },
    description: 'Time from first user interaction to browser response',
  },
  
  // Recording Performance
  RECORDING_START_TIME: {
    name: 'Recording Start Time',
    category: MetricCategory.RECORDING,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 500,
      acceptable: 1000,
      poor: 2000,
    },
    description: 'Time from click to recording start',
  },
  RECORDING_STOP_TIME: {
    name: 'Recording Stop Time',
    category: MetricCategory.RECORDING,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 500,
      acceptable: 1000,
      poor: 2000,
    },
    description: 'Time from stop click to recording complete',
  },
  CHUNK_PROCESSING_TIME: {
    name: 'Chunk Processing Time',
    category: MetricCategory.RECORDING,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 100,
      acceptable: 300,
      poor: 500,
    },
    description: 'Time to process each recording chunk',
  },
  
  // Upload Performance
  UPLOAD_START_TIME: {
    name: 'Upload Start Time',
    category: MetricCategory.UPLOAD,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 200,
      acceptable: 500,
      poor: 1000,
    },
    description: 'Time from chunk ready to upload initiation',
  },
  UPLOAD_THROUGHPUT: {
    name: 'Upload Throughput',
    category: MetricCategory.UPLOAD,
    unit: MetricUnit.MEGABYTES,
    threshold: {
      good: 10,
      acceptable: 5,
      poor: 1,
    },
    description: 'Upload speed in MB/s',
  },
  UPLOAD_SUCCESS_RATE: {
    name: 'Upload Success Rate',
    category: MetricCategory.UPLOAD,
    unit: MetricUnit.PERCENTAGE,
    threshold: {
      good: 99,
      acceptable: 95,
      poor: 90,
    },
    description: 'Percentage of successful uploads',
  },
  
  // Canvas Performance
  CANVAS_RENDER_TIME: {
    name: 'Canvas Render Time',
    category: MetricCategory.CANVAS,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 16,
      acceptable: 33,
      poor: 50,
    },
    description: 'Time to render canvas frame (target 60fps = 16ms)',
  },
  CANVAS_RESIZE_TIME: {
    name: 'Canvas Resize Time',
    category: MetricCategory.CANVAS,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 100,
      acceptable: 300,
      poor: 500,
    },
    description: 'Time to resize and re-render canvas',
  },
  CANVAS_FPS: {
    name: 'Canvas FPS',
    category: MetricCategory.CANVAS,
    unit: MetricUnit.FPS,
    threshold: {
      good: 60,
      acceptable: 30,
      poor: 15,
    },
    description: 'Canvas rendering frame rate',
  },
  
  // Phase 4: Canvas Performance Enhancement Metrics
  RENDER_FRAME_DURATION: {
    name: 'Render Frame Duration',
    category: MetricCategory.CANVAS,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 20,
      acceptable: 28,
      poor: 33,
    },
    description: 'Time for canvas.renderAll() to complete (target <28ms for 30fps budget)',
  },
  FRAME_LATENCY_JITTER: {
    name: 'Frame Latency Jitter',
    category: MetricCategory.CANVAS,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 5,
      acceptable: 10,
      poor: 20,
    },
    description: 'Variance in frame arrival timing',
  },
  MSTG_BACKPRESSURE: {
    name: 'MSTG Backpressure',
    category: MetricCategory.CANVAS,
    unit: MetricUnit.COUNT,
    threshold: {
      good: 1,
      acceptable: 2,
      poor: 3,
    },
    description: 'Number of pending MSTG writes (high value indicates encoder overwhelmed)',
  },
  AUDIO_VIDEO_DRIFT: {
    name: 'Audio Video Drift',
    category: MetricCategory.RECORDING,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 20,
      acceptable: 40,
      poor: 80,
    },
    description: 'Drift between AudioContext time and MSTG frame timestamps',
  },
  FRAMES_DUPLICATED_RATIO: {
    name: 'Frames Duplicated Ratio',
    category: MetricCategory.CANVAS,
    unit: MetricUnit.PERCENTAGE,
    threshold: {
      good: 2,
      acceptable: 5,
      poor: 10,
    },
    description: 'Percentage of frames that were duplicated due to render budget overrun',
  },
  
  // Database Operations
  DB_QUERY_TIME: {
    name: 'Database Query Time',
    category: MetricCategory.DATABASE,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 50,
      acceptable: 100,
      poor: 300,
    },
    description: 'Time for database query to complete',
  },
  DB_WRITE_TIME: {
    name: 'Database Write Time',
    category: MetricCategory.DATABASE,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 100,
      acceptable: 300,
      poor: 500,
    },
    description: 'Time for database write to complete',
  },
  INDEXEDDB_READ_TIME: {
    name: 'IndexedDB Read Time',
    category: MetricCategory.DATABASE,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 20,
      acceptable: 50,
      poor: 100,
    },
    description: 'Time to read from IndexedDB',
  },
  INDEXEDDB_WRITE_TIME: {
    name: 'IndexedDB Write Time',
    category: MetricCategory.DATABASE,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 30,
      acceptable: 100,
      poor: 200,
    },
    description: 'Time to write to IndexedDB',
  },
  
  // Worker Performance
  WORKER_INIT_TIME: {
    name: 'Worker Initialization Time',
    category: MetricCategory.WORKER,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 200,
      acceptable: 500,
      poor: 1000,
    },
    description: 'Time to initialize web worker',
  },
  WORKER_MESSAGE_LATENCY: {
    name: 'Worker Message Latency',
    category: MetricCategory.WORKER,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 10,
      acceptable: 50,
      poor: 100,
    },
    description: 'Time from message send to worker response',
  },
  ENCODER_PROCESSING_TIME: {
    name: 'Encoder Processing Time',
    category: MetricCategory.WORKER,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 100,
      acceptable: 300,
      poor: 500,
    },
    description: 'Time for encoder worker to process chunk',
  },
  
  // User Interaction
  BUTTON_RESPONSE_TIME: {
    name: 'Button Response Time',
    category: MetricCategory.INTERACTION,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 100,
      acceptable: 300,
      poor: 500,
    },
    description: 'Time from button click to UI feedback',
  },
  MODAL_OPEN_TIME: {
    name: 'Modal Open Time',
    category: MetricCategory.INTERACTION,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 200,
      acceptable: 500,
      poor: 1000,
    },
    description: 'Time from trigger to modal fully rendered',
  },
  PAGE_TRANSITION_TIME: {
    name: 'Page Transition Time',
    category: MetricCategory.INTERACTION,
    unit: MetricUnit.MILLISECONDS,
    threshold: {
      good: 300,
      acceptable: 1000,
      poor: 2000,
    },
    description: 'Time to transition between pages',
  },
};

/**
 * Get metric by name
 */
export function getMetric(name: string): PerformanceMetric | undefined {
  return PERFORMANCE_METRICS[name];
}

/**
 * Get all metrics in a category
 */
export function getMetricsByCategory(category: MetricCategory): PerformanceMetric[] {
  return Object.values(PERFORMANCE_METRICS).filter(m => m.category === category);
}

/**
 * Evaluate metric against threshold
 */
export function evaluateMetric(metricName: string, value: number): 'good' | 'acceptable' | 'poor' | 'unknown' {
  const metric = getMetric(metricName);
  if (!metric || !metric.threshold) return 'unknown';
  
  const { good, acceptable, poor } = metric.threshold;
  
  // For CLS, lower is better
  if (metricName === 'CLS') {
    if (value <= good) return 'good';
    if (value <= acceptable) return 'acceptable';
    return 'poor';
  }
  
  // For FPS, higher is better
  if (metric.unit === MetricUnit.FPS) {
    if (value >= good) return 'good';
    if (value >= acceptable) return 'acceptable';
    return 'poor';
  }
  
  // For percentage (like success rate), higher is better
  if (metric.unit === MetricUnit.PERCENTAGE) {
    if (value >= good) return 'good';
    if (value >= acceptable) return 'acceptable';
    return 'poor';
  }
  
  // For all other metrics (time-based), lower is better
  if (value <= good) return 'good';
  if (value <= acceptable) return 'acceptable';
  return 'poor';
}
